import { createContext, use, useCallback, useEffect, useRef, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { subscribeToUnauthorized } from '@/lib/session-events'

export const SESSION_EXPIRED_NOTICE = 'Your session expired due to inactivity. Sign in again.'

const SessionContext = createContext(null)

export function SessionProvider({ initial, children }) {
  const [currentUser, setCurrentUser] = useState(initial?.currentUser ?? null)
  const [csrfToken, setCsrfToken] = useState(initial?.csrfToken ?? null)
  const [stepOneFlowVersion] = useState(initial?.stepOneFlowVersion ?? 'guarded_v4')
  const [sessionNotice, setSessionNotice] = useState(null)
  const refreshSequenceRef = useRef(0)
  const focusRefreshRef = useRef(null)

  const postJson = (path, body, method = 'POST') =>
    apiFetch(path, { method, csrfToken, body })

  const storeCurrentUser = (user) => {
    setCurrentUser(user)
    return user
  }

  const storeAuthenticatedSession = (response) => {
    refreshSequenceRef.current += 1
    setCurrentUser(response.user)
    setCsrfToken(response.csrf_token)
    setSessionNotice(null)
    return response.user
  }

  const signIn = async ({ email, password }) => {
    const res = await postJson('/users/sign_in', { user: { email, password } })
    if (res.two_factor_required) return { twoFactorRequired: true }

    return storeAuthenticatedSession(res)
  }

  const signUp = (values) => postJson('/users', { user: values })

  const signOut = async () => {
    try {
      const response = await postJson('/users/sign_out', undefined, 'DELETE')
      refreshSequenceRef.current += 1
      setCurrentUser(null)
      setCsrfToken(response.csrf_token)
      setSessionNotice(null)
    } catch (error) {
      if (error.status !== 401) throw error
      setCurrentUser(null)
    }
  }

  const requestPasswordReset = ({ email }) =>
    postJson('/users/password', { user: { email } })

  const resetPassword = ({ token, password, password_confirmation }) =>
    postJson(
      '/users/password',
      { user: { reset_password_token: token, password, password_confirmation } },
      'PUT',
    )

  const refresh = useCallback(async () => {
    const sequence = refreshSequenceRef.current + 1
    refreshSequenceRef.current = sequence

    try {
      const user = await apiFetch('/users/me')
      if (!user?.id) throw new Error('Invalid session response')
      if (sequence === refreshSequenceRef.current) setCurrentUser(user)
      return user
    } catch (error) {
      if (error.status !== 401) throw error
      if (sequence === refreshSequenceRef.current) setCurrentUser(null)
      return null
    }
  }, [])

  const expireSession = useCallback(() => {
    refreshSequenceRef.current += 1
    setCurrentUser(null)
    setSessionNotice(SESSION_EXPIRED_NOTICE)
  }, [])

  const currentUserId = currentUser?.id

  useEffect(() => {
    if (!currentUserId) return undefined
    return subscribeToUnauthorized(expireSession)
  }, [currentUserId, expireSession])

  useEffect(() => {
    if (!currentUserId) return undefined

    const reconcileSession = () => {
      if (document.visibilityState === 'hidden' || focusRefreshRef.current) return

      const request = refresh()
      focusRefreshRef.current = request
      request
        .catch(() => null)
        .finally(() => {
          if (focusRefreshRef.current === request) focusRefreshRef.current = null
        })
    }

    window.addEventListener('focus', reconcileSession)
    document.addEventListener('visibilitychange', reconcileSession)

    return () => {
      window.removeEventListener('focus', reconcileSession)
      document.removeEventListener('visibilitychange', reconcileSession)
      focusRefreshRef.current = null
    }
  }, [currentUserId, refresh])

  const updateProfile = async ({ first_name, last_name, email }) => {
    const user = await postJson(
      '/users/profile',
      { user: { first_name, last_name, email } },
      'PATCH',
    )
    return storeCurrentUser(user)
  }

  const updatePassword = async ({ current_password, password, password_confirmation }) => {
    const user = await postJson(
      '/users/password_change',
      { user: { current_password, password, password_confirmation } },
      'PATCH',
    )
    return storeCurrentUser(user)
  }

  const updateSessionTimeout = async ({ session_timeout_hours }) => {
    const user = await postJson(
      '/users/session_timeout',
      { user: { session_timeout_hours } },
      'PATCH',
    )
    return storeCurrentUser(user)
  }

  const acceptDisclaimer = async ({ version }) => {
    const response = await postJson('/api/v1/disclaimer-acceptance', {
      disclaimer_acceptance: { acknowledged: true, version },
    })
    return storeCurrentUser(response.user)
  }

  const verifyTwoFactor = async ({ code }) => {
    const res = await postJson('/users/two_factor', { code })
    return storeAuthenticatedSession(res)
  }

  const verifyTwoFactorBackup = async ({ code }) => {
    const res = await postJson('/users/two_factor/backup', { code })
    return storeAuthenticatedSession(res)
  }

  const beginTwoFactorSetup = () =>
    apiFetch('/api/v1/two_factor/setup', { csrfToken })

  const confirmTwoFactor = async ({ code }) => {
    const res = await postJson('/api/v1/two_factor/confirm', { code })
    return storeCurrentUser(res.user)
  }

  const disableTwoFactor = async ({ code, backup_code }) => {
    const res = await postJson('/api/v1/two_factor/disable', { code, backup_code })
    return storeCurrentUser(res.user)
  }

  const regenerateBackupCodes = async ({ code, backup_code }) =>
    postJson('/api/v1/two_factor/regenerate_backup_codes', { code, backup_code })

  return (
    <SessionContext
      value={{
        currentUser,
        csrfToken,
        sessionNotice,
        stepOneFlowVersion,
        signIn,
        signUp,
        signOut,
        requestPasswordReset,
        resetPassword,
        refresh,
        updateProfile,
        updatePassword,
        updateSessionTimeout,
        acceptDisclaimer,
        verifyTwoFactor,
        verifyTwoFactorBackup,
        beginTwoFactorSetup,
        confirmTwoFactor,
        disableTwoFactor,
        regenerateBackupCodes,
      }}
    >
      {children}
    </SessionContext>
  )
}

export function useSession() {
  const ctx = use(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside SessionProvider')
  return ctx
}
